import type { Alert } from '@aloft/toolkit-shared';
import {
  getAlertCommentaryKey,
  isEditableQualityControlFieldTitle,
  normalizeQualityControlFieldTitle,
} from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  InfoIcon,
  Input,
  RadioGroup,
  Select,
  SimpleDollarInput,
  Typography,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { AlertsContext } from '../../referenceView/AlertsSheet/hooks/alertsContext.ts';
import type { QualityControlFieldDefinition } from './qualityControlFieldRegistry.ts';
import { getQualityControlFieldDefinition } from './qualityControlFieldRegistry.ts';
import { useQualityControlField } from './useQualityControlField.ts';

const ALERT_COMMENTARY_PREFIX = 'requiredFields.alertsCommentary.';

const QualityControlReviewTextarea = ({
  value,
  onChange,
  onBlur,
}: {
  value: string;
  onChange: (nextValue: string) => void;
  onBlur: () => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight, value]);

  return (
    <Textarea
      ref={textareaRef}
      size="sm"
      rows={1}
      value={value}
      resize="vertical"
      overflow="hidden"
      sx={{ minHeight: 'unset' }}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
    />
  );
};

const QualityControlReviewFieldControl = ({
  definition,
  value,
  onChange,
  onBlur,
}: {
  definition: QualityControlFieldDefinition;
  value: string;
  onChange: (nextValue: string) => void;
  onBlur: () => void;
}) => {
  if (definition.kind === 'textarea') {
    return <QualityControlReviewTextarea value={value} onChange={onChange} onBlur={onBlur} />;
  }

  if (definition.kind === 'select') {
    return (
      <Select
        size="sm"
        options={definition.options ?? []}
        value={value}
        onChange={(nextValue) => onChange(nextValue ?? '')}
        onBlur={onBlur}
      />
    );
  }

  if (definition.kind === 'radio') {
    return (
      <RadioGroup
        size="sm"
        items={definition.options ?? []}
        value={value}
        onChange={(nextValue) => {
          onChange(nextValue);
          onBlur();
        }}
      />
    );
  }

  if (definition.kind === 'currency') {
    return (
      <SimpleDollarInput
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
    );
  }

  return (
    <Input
      size="sm"
      type="text"
      value={value}
      onChange={onChange}
      onClear={() => onChange('')}
      onBlur={onBlur}
    />
  );
};

export const QualityControlReviewFormField = ({
  fieldTitle,
  isResolved = false,
  onBlur,
}: {
  fieldTitle: string;
  isResolved?: boolean;
  onBlur: () => void;
}) => {
  const normalizedTitle = normalizeQualityControlFieldTitle(fieldTitle);
  const definition = getQualityControlFieldDefinition(normalizedTitle);
  const { title, value, onChange, isEditable } = useQualityControlField(fieldTitle);
  const isAlertCommentary = fieldTitle.startsWith(ALERT_COMMENTARY_PREFIX);
  const alerts = useContext(AlertsContext);

  if (!title || !isEditable || (!definition && !isAlertCommentary)) return null;

  const alertCommentaryKey = isAlertCommentary
    ? fieldTitle.slice(ALERT_COMMENTARY_PREFIX.length)
    : undefined;
  const alert = alertCommentaryKey
    ? Object.values(alerts ?? {})
        .flat()
        .find((candidate: Alert) => getAlertCommentaryKey(candidate) === alertCommentaryKey)
    : undefined;

  return (
    <Flex flexDirection="column" gap="8px" width="100%" alignItems="flex-start">
      <Flex alignItems="center" flexShrink={0} minWidth="128px" gap="4px">
        <Typography variant="h4" color="textSecondary">
          {title}
        </Typography>
        {(definition?.isRequired || isAlertCommentary) && (
          <Typography variant="h4" color="textError">
            {' *'}
          </Typography>
        )}
        {definition?.tooltip && <InfoIcon tooltipContent={definition.tooltip} />}
      </Flex>
      {alert && (
        <Flex flexDirection="column" gap="4px">
          <Typography variant="body1">{alert.message}</Typography>
          <Typography variant="body2" color="surfaceAccent4">
            {alert.fixingInfo.tips}
          </Typography>
        </Flex>
      )}
      <Block flex={1} minWidth="128px" width="100%">
        {isAlertCommentary ? (
          <QualityControlReviewTextarea value={value} onChange={onChange} onBlur={onBlur} />
        ) : (
          <QualityControlReviewFieldControl
            definition={definition!}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
          />
        )}
      </Block>
      {!isResolved &&
        (isAlertCommentary || definition?.kind === 'text' || definition?.kind === 'textarea') && (
        <Typography variant="body2" color="textError">
          Click the textbox to edit content and enable compliance check.
        </Typography>
        )}
    </Flex>
  );
};

export const QualityControlReviewFormCard = ({
  title,
  fieldTitles,
  defaultExpanded = false,
  isResolved = false,
  onFieldBlur,
}: {
  title: string;
  fieldTitles: string[];
  defaultExpanded?: boolean;
  isResolved?: boolean;
  onFieldBlur: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded && !isResolved);
  const editableFieldTitles = fieldTitles
    .map((fieldTitle) =>
      fieldTitle.startsWith(ALERT_COMMENTARY_PREFIX)
        ? fieldTitle
        : normalizeQualityControlFieldTitle(fieldTitle),
    )
    .filter(
      (fieldTitle) =>
        fieldTitle.startsWith(ALERT_COMMENTARY_PREFIX) ||
        isEditableQualityControlFieldTitle(fieldTitle),
    );
  const hasEditableFields = editableFieldTitles.length > 0;

  useEffect(() => {
    if (isResolved && editableFieldTitles.length === 1) setIsExpanded(false);
  }, [editableFieldTitles.length, isResolved]);

  return (
    <Flex
      flexDirection="column"
      gap="12px"
      width="100%"
      backgroundColor="surface1"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="strokeDefault"
      borderRadius="4px"
      paddingX="20px"
      paddingY="16px"
      __dangerDoNotUseSx={{
        boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.02), 0px 2px 2px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        cursor={hasEditableFields ? 'pointer' : undefined}
        onClick={() => {
          if (hasEditableFields) setIsExpanded((current) => !current);
        }}
      >
        <Flex gap="8px" alignItems="center" minWidth={0}>
          {isResolved && <Icon icon="check-circle-filled" size="lg" color="arv" />}
          <Typography variant="h4" color="buttonDefault">
            {title}
          </Typography>
        </Flex>
        {hasEditableFields && <Icon icon={isExpanded ? 'arrow-up-s' : 'arrow-down-s'} size="md" />}
      </Flex>
      {isExpanded && hasEditableFields && (
        <Flex flexDirection="column" gap="12px" width="100%">
          {editableFieldTitles.map((fieldTitle) => (
            <QualityControlReviewFormField
              key={fieldTitle}
              fieldTitle={fieldTitle}
              isResolved={isResolved}
              onBlur={onFieldBlur}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
